import { useLiveQuery } from "dexie-react-hooks"
import type { ChatSession } from "@stockline/shared/types/ai/chat-session"
import { CHAT_NOTIFICATION_TYPE } from "@stockline/shared/types/ai/chat-session"
import { offlineDb } from "../../../lib/offline/db"
import { CHAT_SESSION_TABLE } from "../../../lib/offline/resources/ai/chat-session/db"
import { useActiveChatSession } from "../stores/activeChatSessionStore"

/**
 * Whether a session counts toward the general `/ai` inbox unread badge: an
 * unread, non-deleted session that is NOT a Business-Intelligence brief. BI
 * briefs (`notificationType === "bi"`) are steering-only — they drive the
 * steering nav badge (`useUnreadBiDashboardIds`) and must never inflate the
 * general AI inbox count.
 */
export function isUnreadInboxSession(s: ChatSession): boolean {
  return (
    s.unread === true &&
    !s.deletedAt &&
    s.notificationType !== CHAT_NOTIFICATION_TYPE.BI
  )
}

/**
 * Reactive count of chat sessions with an unseen assistant reply
 * (`session.unread === true`), excluding soft-deleted rows AND the session the
 * user is actively looking at while the tab is focused.
 *
 * `unread` is a boolean (not a Dexie-indexable field), so the count scans +
 * filters in memory — fine, the chat-session store is user-scoped and small.
 *
 * The active+focused exclusion is the same flicker guard the list applies: a
 * reply landing on the chat in front of the user must not bump the badge before
 * the clear-unread push lands. `useClearUnreadOnFocus` reconciles the DB row
 * shortly after; until then this keeps the badge honest.
 */
export function useUnreadChatCount(): number {
  const { activeSessionId, isFocused } = useActiveChatSession()
  const sessions = useLiveQuery(
    () =>
      offlineDb
        .table<ChatSession>(CHAT_SESSION_TABLE)
        .filter(isUnreadInboxSession)
        .toArray(),
    [],
    [] as ChatSession[]
  )
  const rows = sessions ?? []
  const suppressedId = isFocused ? activeSessionId : null
  return rows.filter((s) => s.id !== suppressedId).length
}
